/**
 * NewExpedienteScreen — ruta `/expedientes/nuevo` (operator+).
 *
 * Pantalla completa para crear un expediente individual en la ronda "default"
 * (la bandeja de entrada persistente). Bajo el perfil EXACER la CURP ES el
 * expediente_id (el contrato trata el id como opaco; la regla del perfil lo
 * hace CURP — la misma convención que las carpetas de la carga masiva). Flujo:
 *   1) signUploads declara el `tipo` de cada documento y mint upload URLs.
 *   2) Cada File se PUT directo a su upload_url con progreso por archivo.
 *   3) processSingleExpediente encola el worker para ESTE expediente — el
 *      backend auto-crea la ronda "default" si no existe.
 *
 * Reutiliza la lógica de uploadTypes + putBytes de la pantalla de Operaciones.
 */

import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";

import { api, isApiError, IS_DEMO } from "../api/client";
import type { SignUploadResponse } from "../api/client";
import { DemoBadge } from "../components/DemoBadge";
import { ProblemAlert } from "../components/ProblemAlert";
import { demoSign } from "./operations/demo";
import { isValidCurp } from "./operations/folderScan";
import { putBytes } from "./operations/putBytes";
import {
  contentTypeFor,
  DOC_KIND_LABEL,
  isRequiredDoc,
  newSlots,
  type DocKind,
  type DocSlot,
} from "./operations/uploadTypes";

/** La ronda bandeja de entrada — round_id fijo conocido por el backend. */
const DEFAULT_ROUND_ID = "default";

function slotChip(slot: DocSlot) {
  switch (slot.status) {
    case "done":
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CheckCircleOutlineIcon />}
          label="Cargado"
        />
      );
    case "error":
      return (
        <Chip
          size="small"
          color="error"
          variant="outlined"
          icon={<ErrorOutlineIcon />}
          label="Error"
        />
      );
    case "signing":
      return (
        <Chip size="small" color="info" variant="outlined" label="Firmando…" />
      );
    case "uploading":
      return (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label={`${slot.progress}%`}
        />
      );
    default:
      return slot.file ? (
        <Chip size="small" variant="outlined" label="Listo" />
      ) : (
        <Chip size="small" variant="outlined" label="Sin archivo" />
      );
  }
}

export default function NewExpedienteScreen() {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [curp, setCurp] = useState("");
  const [dob, setDob] = useState("");
  const [slots, setSlots] = useState<DocSlot[]>(newSlots);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [conflict, setConflict] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const fileInputs = useRef<Record<DocKind, HTMLInputElement | null>>({
    ine: null,
    ine_reverso: null,
    certificado: null,
    foto: null,
  });

  const nameValid = fullName.trim().length > 0;
  // La CURP también es el expediente_id (regla del perfil EXACER) — se valida
  // completa en el cliente para que el PROFILE_ID_VIOLATION del servidor nunca dispare.
  const curpValid = isValidCurp(curp.trim());
  const allPicked = slots.every(
    (s) => s.file !== null || !isRequiredDoc(s.kind),
  );
  const canSubmit = nameValid && curpValid && allPicked && !busy;

  function reset() {
    setFullName("");
    setCurp("");
    setDob("");
    setSlots(newSlots());
    setError(null);
    setConflict(false);
  }

  function pickFile(kind: DocKind, file: File | null) {
    setError(null);
    setSlots((prev) =>
      prev.map((s) =>
        s.kind === kind
          ? { ...s, file, status: "pending", progress: 0, error: undefined }
          : s,
      ),
    );
  }

  function setSlot(kind: DocKind, patch: Partial<DocSlot>) {
    setSlots((prev) =>
      prev.map((s) => (s.kind === kind ? { ...s, ...patch } : s)),
    );
  }

  async function handleSubmit() {
    // Bajo EXACER la CURP ES el expediente_id (regla del perfil).
    const id = curp.trim().toUpperCase();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setConflict(false);
    setSuccess(null);

    try {
      // 1) Firmar URLs de carga, declarando el tipo de cada documento.
      setSlots((prev) =>
        prev.map((s) => ({
          ...s,
          status: "signing",
          progress: 0,
          error: undefined,
        })),
      );
      const files = slots
        .filter((s): s is DocSlot & { file: File } => s.file !== null)
        .map((s) => ({
          tipo: s.kind,
          content_type: contentTypeFor(s.file),
        }));

      let signed: SignUploadResponse;
      if (IS_DEMO) {
        signed = demoSign(DEFAULT_ROUND_ID, id, files);
      } else {
        const res = await api.POST("/uploads/sign", {
          body: { round_id: DEFAULT_ROUND_ID, expediente_id: id, files },
        });
        signed = res.data!;
      }

      // 2) PUT cada archivo a su upload_url (emparejado por tipo).
      let anyFailed = false;
      await Promise.all(
        signed.uploads.map(async (u) => {
          const kind = u.tipo as DocKind;
          const slot = slots.find((s) => s.kind === kind);
          if (!slot?.file) return;
          setSlot(kind, { status: "uploading", progress: 0 });
          try {
            await putBytes(
              u.upload_url,
              slot.file,
              contentTypeFor(slot.file),
              (pct) => setSlot(kind, { progress: pct }),
            );
            setSlot(kind, { status: "done", progress: 100 });
          } catch (err) {
            anyFailed = true;
            setSlot(kind, {
              status: "error",
              error: err instanceof Error ? err.message : "Carga fallida.",
            });
          }
        }),
      );

      if (anyFailed) {
        setError(
          new Error(
            "No se pudieron cargar todos los documentos. Revisa e inténtalo de nuevo.",
          ),
        );
        setSlots((prev) =>
          prev.map((s) =>
            s.status === "done" ? { ...s, status: "pending", progress: 0 } : s,
          ),
        );
        return;
      }

      // 3) Registrar identidad + encolar worker. El backend auto-crea la ronda "default".
      if (!IS_DEMO) {
        await api.POST(
          "/rounds/{round_id}/expedientes/{expediente_id}/process",
          {
            params: { path: { round_id: DEFAULT_ROUND_ID, expediente_id: id } },
            body: {
              full_name: fullName.trim(),
              curp: curp.trim(),
              fecha_nacimiento: dob.trim() || null,
            },
          },
        );
      }

      setSuccess(id);
      reset();
    } catch (err: unknown) {
      if (isApiError(err) && err.status === 409) {
        setConflict(true);
      } else if (isApiError(err)) {
        setError(err);
      } else {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      setSlots((prev) =>
        prev.map((s) =>
          s.status === "signing" ? { ...s, status: "pending" } : s,
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      {/* Encabezado */}
      <Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", mb: 0.5 }}
        >
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/")}
            sx={{ mr: 1 }}
          >
            Inicio
          </Button>
          <DemoBadge />
        </Stack>
        <Typography variant="h1">Nuevo expediente</Typography>
        <Typography variant="body2" color="text.secondary">
          El expediente se agrega a la bandeja de entrada y se procesa de
          inmediato con el mismo pipeline de validación.
        </Typography>
      </Box>

      {/* Banner de éxito */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          <AlertTitle>Expediente encolado</AlertTitle>
          <strong>{success}</strong> se está procesando. Los resultados
          aparecerán en el monitor de la ronda <em>default</em>.
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<MonitorHeartIcon />}
              onClick={() => navigate("/rounds/default/monitor")}
            >
              Ver monitor
            </Button>
          </Box>
        </Alert>
      )}

      {conflict && (
        <Alert severity="warning" onClose={() => setConflict(false)}>
          <AlertTitle>Ronda en proceso</AlertTitle>
          La ronda <em>default</em> tiene una corrida por lotes en curso. Espera
          a que termine.
        </Alert>
      )}

      {error && <ProblemAlert error={error} />}

      {/* Formulario */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Nombre completo"
            placeholder="Como aparece en sus documentos"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            size="small"
            required
            fullWidth
            disabled={busy}
            error={!nameValid && fullName.length > 0}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="CURP"
              placeholder="18 caracteres"
              value={curp}
              onChange={(e) => setCurp(e.target.value.toUpperCase())}
              size="small"
              required
              fullWidth
              disabled={busy}
              error={!curpValid && curp.length > 0}
              helperText="Es el ID del expediente bajo el perfil EXACER."
            />
            <TextField
              label="Fecha de nacimiento"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              size="small"
              fullWidth
              disabled={busy}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { max: new Date().toISOString().slice(0, 10) },
              }}
              helperText="Opcional."
            />
          </Stack>

          {/* Documentos */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Documentos
            </Typography>
            <Stack spacing={1.5}>
              {slots.map((slot) => (
                <Box key={slot.kind}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ minWidth: 0, alignItems: "center" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, width: 100, flexShrink: 0 }}
                      >
                        {DOC_KIND_LABEL[slot.kind]}
                        {!isRequiredDoc(slot.kind) && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {" "}
                            (opc.)
                          </Typography>
                        )}
                      </Typography>
                      <input
                        ref={(el) => {
                          fileInputs.current[slot.kind] = el;
                        }}
                        type="file"
                        accept="image/*,application/pdf"
                        hidden
                        onChange={(e) =>
                          pickFile(slot.kind, e.target.files?.[0] ?? null)
                        }
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileOutlinedIcon />}
                        onClick={() => fileInputs.current[slot.kind]?.click()}
                        disabled={busy || slot.status === "done"}
                      >
                        {slot.file ? "Cambiar" : "Elegir"}
                      </Button>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 180,
                        }}
                        title={slot.file?.name}
                      >
                        {slot.file?.name ?? "Ningún archivo"}
                      </Typography>
                    </Stack>
                    {slotChip(slot)}
                  </Stack>
                  {slot.status === "uploading" && (
                    <LinearProgress
                      variant="determinate"
                      value={slot.progress}
                      sx={{ mt: 0.75, borderRadius: 1 }}
                    />
                  )}
                  {slot.status === "error" && slot.error && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {slot.error}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Acciones */}
      <Stack direction="row" spacing={1.5} sx={{ justifyContent: "flex-end" }}>
        <Button onClick={() => navigate("/")} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<PersonAddAlt1Icon />}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {busy ? "Procesando…" : "Crear y procesar"}
        </Button>
      </Stack>
    </Stack>
  );
}
